import { Avatar, Stack } from '@mui/material'

import { formatAdvisorName, toAmountString, toDateStringInTimezone } from '../../utils'
import { Box, Divider, Icons, Text } from '../../components'

// Shared between the Investment Account tab's portfolio card
// (investment-account.jsx) and the per-portfolio "View Activity" drill-down
// (investment-portfolio-activity.jsx) -- same content, same formatting,
// rendered in exactly one place so the two views can't drift apart.
//
// Layout per the FLIGHT-296 mockup (Tanis's Canva redesign, linked on the
// ticket): Financial Firm leads as the bold heading with Advisor/Advisory
// Team/Account below it on the left, Investment Balance/Last Statement Date
// right-aligned on the right -- a reshuffle of the same fields the old
// single-column layout showed (Financial Advisor no longer leads), not new
// data.
//
// `actions` (the "View activity"/"Initiate Transfer" buttons on the
// Investment Account tab's cards) renders inside this same left text column,
// not as a sibling block underneath with a hand-tuned margin guessing the
// avatar's width -- that way it's flush with the text itself no matter what
// the avatar ends up sized at. investment-portfolio-activity.jsx's
// drill-down title omits `actions` (those buttons sit in the DataGrid
// toolbar there instead) so it renders unchanged.
//
// `evenColumns` splits the left and right columns 1fr:1fr instead of the
// default 3fr:1fr. The 3fr:1fr split suits the full-width cards, but inside
// the narrower Request Transfer/Liquidation dialog (initiate-transfer-
// dialog.jsx) it leaves the right column cramped.
export function PortfolioSummary({ portfolio, actions, evenColumns = false }) {
  const advisorName = formatAdvisorName(portfolio.financialAdvisor)
  const firmName = portfolio.financialFirm?.name

  return (
    // CSS Grid, not a flex Stack, for the left/divider/right split -- every
    // Stack in this app defaults to width: 100% (the MuiStack theme override
    // in mui.config.js), which fights a flex row's own basis/grow math the
    // moment there's more than one flexible sibling: the left column was
    // starving down toward zero width (wrapping word-by-word) while the
    // fixed-width right column and Divider held their ground and got pushed
    // past the card's edge. Grid track sizing isn't subject to that -- the
    // left column's own Stack filling its grid cell at width: 100% is
    // exactly what's wanted here, not a conflict to fight.
    //
    // Left:right split is 3fr:1fr (not the right one sized to its own
    // content, and not an even 1fr:1fr split) so the divider and the right
    // column land at the card's 3/4 mark, instead of the right column being
    // squeezed to its own content width and ending up flush against the
    // card's right edge, or starting at dead center.
    //
    // flexGrow: 1 (not width: '100%') -- on the Investment Account tab's
    // list, this Box sits in normal block flow (inside PortfolioCard's plain
    // Box) and already fills the available width by default; flexGrow is a
    // no-op there. On the single-account drill-down, this same component
    // renders as the DataGrid toolbar's `title`, as a flex row sibling of
    // the Back arrow IconButton (investment-portfolio-activity.jsx) -- a
    // block-level grid container there shrinks to fit its own content
    // instead of filling the row, so the 3fr/1fr split ends up computed
    // against that much narrower width. width: '100%' would "fix" that but
    // reintroduce the exact bug fixed above (claiming the full row width
    // and fighting the IconButton for it, same as the Divider/right-column
    // fight this whole approach replaced) -- flexGrow only kicks in inside
    // a flex context, ceding space to that sibling instead of fighting it.
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, ${evenColumns ? 1 : 3}fr) auto minmax(0, 1fr)`,
        columnGap: 2,
        flexGrow: 1,
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
        {/* First letter of the Financial Firm, not a generic icon -- the
            mockup's explicit recommendation if the avatar circles stay. */}
        <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 40, height: 40 }}>
          {firmName ? firmName.charAt(0).toUpperCase() : '?'}
        </Avatar>
        {/* spacing, not each line's own margin -- one consistent vertical
            gap between Financial Firm/Advisor/Advisory Team/Account instead
            of ad hoc per-line margins that'd need to stay in sync by hand. */}
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Text.H6>Financial Firm: {firmName || 'Unknown Firm'}</Text.H6>
          <Text.Body color="text.secondary">Advisor: {advisorName || 'Unassigned Advisor'}</Text.Body>
          {portfolio.advisoryTeam?.name && (
            <Text.Body color="text.secondary">Advisory Team: {portfolio.advisoryTeam.name}</Text.Body>
          )}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 'auto', color: 'text.secondary' }}>
            {/* FontAwesome's wallet, not the MUI icon -- SVG fill defaults to
                currentColor, so it picks up this row's text.secondary. */}
            <Icons.Wallet style={{ fontSize: '1rem' }} />
            {/* Text.Body, not Text.Caption -- matches the other investment
                labels (Advisor, Investment Balance, etc.) instead of
                Caption's smaller default. */}
            <Text.Body color="text.secondary">Account {portfolio.custodialNumber}</Text.Body>
          </Stack>
          {actions && (
            <Stack direction="row" spacing={2} sx={{ width: 'auto' }}>
              {actions}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 2, borderColor: 'grey.400' }} />
      {/* pl on top of the grid's own columnGap -- widens just the gap on
          this side of the divider, not the left column's side too. */}
      <Box sx={{ pl: 2 }}>
        {/* Text.Body, not Text.Caption -- matches the "Advisor: ..." label's
            size on the left column instead of Caption's smaller default. */}
        <Text.Body color="text.secondary">Investment Balance</Text.Body>
        <Text.H6>{toAmountString(portfolio.balance)}</Text.H6>
        <Text.Body color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last Statement Date
        </Text.Body>
        <Text.Body sx={{ fontWeight: 'bold' }}>
          {/* Last Statement Date is a calendar date stored as UTC midnight
              (set via a plain <input type="date">) -- formatting with the
              viewer's local timezone can roll it back a day for anyone
              behind UTC, so read it back out in UTC explicitly, same fix
              as investment-transfers/formatDate.js. */}
          {portfolio.lastStatementDate ? toDateStringInTimezone(portfolio.lastStatementDate, 'UTC') : '--'}
        </Text.Body>
      </Box>
    </Box>
  )
}
